import React from 'react';
import { Button, Spinner, Alert } from '@parsina/framework';
import { Coins, LogIn } from 'lucide-react';
import logo from 'url:../../assets/logo-white.png';

import { C, CT, GRADIENT_BRAND_DARK, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { LOGIN_FEATURES } from './Login.shared.js';

// ورود — the desktop sign-in screen.
//
// Two columns: what the app is, on the brand gradient; and the one control that
// starts the OIDC redirect. There is nothing else on it — no password field, no
// «ثبت‌نام» tab, no forgot-password link — because login and signup are the SAME
// phone-first, OTP flow inside Keycloak, and every one of those controls would be
// a second, wrong entry point to it.
//
// The redirect goes to the `parsina-student` client, NOT the `parsina-web` the
// doctor and clinic panels share: a token minted for that one opens
// doctor.parsinabot.ir, and the audience separation IS this app's gate
// (CORE.md §7). src/lib/auth.js owns that constant.

export default function LoginWeb({ onSignIn, busy = false, notice = '' }) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        minHeight: '100vh',
        background: GRADIENT_BRAND_DARK,
        padding: 24,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 380px)',
          gap: 40,
          width: '100%',
          maxWidth: 940,
          alignItems: 'center',
        }}
      >
        {/* ── What this is ── */}
        <div style={{ color: '#fff', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
            <img src={logo} alt="" style={{ height: '2.6rem', width: 'auto', objectFit: 'contain' }} />
            <div>
              <div style={{ fontSize: 19, fontWeight: 800 }}>{t('nav.brand.name')}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
                {t('nav.brand.panel')}
              </div>
            </div>
          </div>

          <h1 style={{ margin: '0 0 10px', fontSize: 30, fontWeight: 800, lineHeight: 1.4 }}>
            {t('login.title')}
          </h1>
          <p style={{ margin: '0 0 28px', fontSize: 15, lineHeight: 1.9, color: 'rgba(255,255,255,0.72)' }}>
            {t('login.sub')}
          </p>

          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {LOGIN_FEATURES.map(({ id, titleKey, bodyKey, Icon }) => (
              <li key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: 'rgba(255,255,255,0.12)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} aria-hidden />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700 }}>{t(titleKey)}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.8, color: 'rgba(255,255,255,0.6)' }}>
                    {t(bodyKey)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* ── The one control ── */}
        <div
          style={{
            background: C.card,
            borderRadius: 18,
            padding: 30,
            boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
            textAlign: 'center',
          }}
        >
          {/*
            «۵۰۰ پارسه، رایگان، هر ماه» — stated before sign-in because it is true
            without any action: the wallet auto-creates on the first balance read
            and refills at each calendar-month rollover. There is deliberately no
            «دریافت اعتبار» button anywhere in this app to go with it (CORE.md §8).
          */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: soft(C.teal, 0.14),
              color: CT.accentInk,
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 12.5,
              fontWeight: 700,
              marginBottom: 18,
            }}
          >
            <Coins size={14} aria-hidden />
            {t('login.free')}
          </div>

          {/* `invalid_redirect_uri` — this origin is not registered on the
              `parsina-student` client. Retrying cannot fix it, so the button
              is not offered as the answer: the sentence is (core.md §7.1). */}
          {notice && (
            <Alert variant="warning" style={{ marginBottom: 14, textAlign: 'start' }} data-testid="login-notice">
              {notice}
            </Alert>
          )}

          <Button
            size="lg"
            onClick={onSignIn}
            disabled={busy}
            className="w-full"
            data-testid="login-action"
          >
            {busy ? <Spinner size={16} color="#fff" /> : <LogIn size={17} aria-hidden />}
            {busy ? t('login.working') : t('login.action')}
          </Button>

          <p style={{ margin: '14px 0 0', fontSize: 12, lineHeight: 1.9, color: C.textMuted }}>
            {t('login.hint')}
          </p>
        </div>
      </div>
    </div>
  );
}
